import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { fetchEngageTenants, fetchEngageTenantsSummary } from './engage-tenants-api';
import { daysToDateRange } from '../../../lib/date-range-utils';

export const ENGAGE_TENANTS_PAGE_SIZE = 25;

export function useEngageTenants(selectedDays: number) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<Record<number, string | undefined>>({});

  const [prevSelectedDays, setPrevSelectedDays] = useState(selectedDays);
  if (selectedDays !== prevSelectedDays) {
    setPrevSelectedDays(selectedDays);
    setCurrentPage(1);
    setPageCursors({});
  }

  const cursor = pageCursors[currentPage];
  const dateRange = daysToDateRange(selectedDays);

  const tenantsQuery = useQuery({
    queryKey: ['engageTenants', selectedDays, currentPage, cursor],
    queryFn: () =>
      fetchEngageTenants({
        cursor,
        limit: ENGAGE_TENANTS_PAGE_SIZE,
        ...dateRange,
      }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const summaryQuery = useQuery({
    queryKey: ['engageTenantsSummary', selectedDays, dateRange.startDate, dateRange.endDate],
    queryFn: () => fetchEngageTenantsSummary(dateRange),
    staleTime: 60_000,
  });

  const goToNextPage = useCallback(() => {
    const nextCursor = tenantsQuery.data?.cursor;
    if (tenantsQuery.data?.hasNextPage && nextCursor) {
      setPageCursors(prev => ({ ...prev, [currentPage + 1]: nextCursor }));
      setCurrentPage(page => page + 1);
    }
  }, [tenantsQuery.data, currentPage]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(page => page - 1);
    }
  }, [currentPage]);

  return {
    data: tenantsQuery.data?.data ?? [],
    summary: summaryQuery.data,
    isLoading: tenantsQuery.isLoading,
    isFetching: tenantsQuery.isFetching || summaryQuery.isFetching,
    summaryIsLoading: summaryQuery.isLoading,
    summaryError: summaryQuery.error,
    error: tenantsQuery.error,
    hasNextPage: tenantsQuery.data?.hasNextPage ?? false,
    currentPage,
    goToNextPage,
    goToPreviousPage,
  };
}
